import React from 'react';
import { motion } from 'framer-motion';
import ProjectStatsBadge from '../../../components/ProjectStatsBadge';
import ShareProjectButton from '../../../components/ShareProjectButton/ShareProjectButton';
import './vista-holidays.css';
import vistaHolidaysImg from '../../../assets/images/Vista holidays Hero.png';

const VistaHolidays = () => {
  return (
    <motion.div 
      className="project-detail-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
    >
      <motion.h1 
        className="project-heading"
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.2 }}
      >
        Vista Holidays <span>Travel</span> – AI-Powered Travel Platform ✈️🌍
      </motion.h1>

      <motion.p 
        className="last-updated"
        initial={{ x: 50, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.4 }}
      ><br />
        Last Updated On: 16 September 2026
      </motion.p>

      <div className="project-content">
        <motion.div 
          className="project-left"
          initial={{ x: -100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <img src={vistaHolidaysImg} alt="Vista Holidays Platform" className="project-image" />
          <h2 className="project-subtitle">Vista Holidays Platform</h2>
          <span className="project-category">Software Applications</span>
          <p className="project-author">Author: Praveen Kumar B</p>

          <ProjectStatsBadge docId="vistaHolidays" trackView={true} />

          <div className="project-buttons">
            <a
              href="https://vista-holidays-frontend.vercel.app"
              className="visit-project-button"
              target="_blank"
              rel="noopener noreferrer"
            >
              → Visit the Project
            </a>
            <a
              href="https://github.com/Praveenkumar-king"
              className="visit-project-button"
              target="_blank"
              rel="noopener noreferrer"
            >
              → View on GitHub
            </a>
            <ShareProjectButton
              project={{
                id: "vistaHolidays",
                title: "Vista Holidays",
                path: "/projects/projectdetail/vista-holidays"
              }}
            />
          </div>
        </motion.div>

        <motion.div 
          className="project-right"
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <h3 className="section-title">✈️ Overview</h3>
          <p className="overview-description">
            <strong>Vista Holidays</strong> is a modern full-stack AI-powered travel platform designed to make trip discovery, planning, and booking simple and personalized. Users can explore destinations, discover famous places, check real-time weather, interact with an AI Travel Assistant, generate personalized itineraries, and securely book travel packages through an integrated payment system. 🏖️🗺️
          </p>
          <p className="overview-description">
            The platform combines AI, real-time APIs, secure authentication, online payments, cloud media storage, and travel management into one unified experience. ⚡💼
          </p>

          <h3 className="section-title">🌍 Key Features</h3>
          <ul className="feature-list">
            <li><strong>Destination Explorer</strong> 🧭 — Search and explore curated destinations with detailed travel information.</li>
            <li><strong>Famous Places</strong> 🏛️ — Discover popular attractions and landmarks for each destination.</li>
            <li><strong>Real-Time Weather</strong> ⛅ — View current weather information for travel destinations.</li>
            <li><strong>AI Travel Assistant</strong> 🤖 — Get AI-powered travel guidance and destination assistance.</li>
            <li><strong>AI Itinerary Planner</strong> 📅 — Generate personalized multi-day travel plans.</li>
            <li><strong>User Authentication</strong> 🔐 — Secure registration, login, email verification, and protected user access.</li>
            <li><strong>User Dashboard</strong> 👤 — Manage profile, bookings, saved destinations, activities, and travel information.</li>
            <li><strong>Travel Booking</strong> 🎟️ — Select packages, stay duration, and number of travelers with dynamic pricing.</li>
            <li><strong>Razorpay Payments</strong> 💳 — Complete travel bookings through secure online payments.</li>
            <li><strong>My Bookings</strong> 🎒 — View and manage personal booking history and upcoming trips.</li>
          </ul>

          <h3 className="section-title">🚀 Advanced Features</h3>
          <ul className="feature-list">
            <li><strong>Dynamic Booking & Pricing</strong> 📊 — Calculates booking totals based on package, duration, and number of travelers.</li>
            <li><strong>Payment Verification & Webhooks</strong> 🛡️ — Backend-based Razorpay payment verification with webhook support.</li>
            <li><strong>Cloudinary Media Management</strong> ☁️ — Secure profile-image and product-update image uploads.</li>
            <li><strong>Automated Email System</strong> 📧 — Verification, welcome, booking confirmation, ticket updates, and admin notification emails through Brevo SMTP.</li>
            <li><strong>Support Ticket System</strong> 🎫 — Contact and feedback submissions with unique ticket IDs, status tracking, timelines, and admin responses.</li>
            <li><strong>Product Updates</strong> 📢 — Admin-managed What's New releases with multi-slide Cloudinary content and per-user seen tracking.</li>
            <li><strong>Platform Announcements</strong> 📣 — Audience-based announcements with activation and expiry controls.</li>
            <li><strong>Maintenance Mode</strong> 🛠️ — Administrators can temporarily restrict normal user access while maintaining admin access.</li>
            <li><strong>Admin Transactions</strong> 💰 — Monitor bookings, payment status, transactions, and revenue.</li>
            <li><strong>Profile & Security Management</strong> 🔒 — Profile updates, password management, account verification, and secure session controls.</li>
            <li><strong>Responsive Experience</strong> 📱 — Optimized for desktop, tablet, and mobile devices.</li>
          </ul>

          <h3 className="section-title">💡 Why Vista Holidays?</h3>
          <p className="overview-description">
            Vista Holidays brings multiple parts of the travel journey into a single platform instead of separating discovery, weather, planning, AI assistance, and booking across different services. 🌐✈️
          </p>
          <p className="overview-description">
            It is designed around a simple idea:
          </p>
          <ul className="how-it-works-list">
            <li>🔍 <strong>Discover a destination</strong> → 📖 <strong>Understand it</strong> → 🗓️ <strong>Plan your journey</strong> → 💳 <strong>Book your trip</strong>.</li>
          </ul>
          <p className="overview-description">
            The platform also demonstrates how modern technologies such as AI, real-time APIs, cloud services, secure authentication, payment gateways, and scalable backend architecture can be combined to build a complete real-world travel application. 💡🚀
          </p>

          <h3 className="section-title">🎯 Conclusion</h3>
          <p className="overview-description">
            <strong>Vista Holidays</strong> is more than a travel website; it is a complete digital travel experience covering destination discovery, intelligent planning, real-time information, secure booking, payments, customer support, and administration. ✨🌏
          </p>
          <p className="overview-description">
            Built with a modern full-stack architecture, the project demonstrates practical implementation of <strong>React, Node.js, Express, MongoDB, AI services, Razorpay, Cloudinary, Brevo SMTP</strong>, and secure authentication to create a scalable and user-focused travel platform. 🚀🌴
          </p>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default VistaHolidays;
